/**
 * Browser Fingerprint Generator
 * Creates a unique hash based on browser/device characteristics
 * Used to identify anonymous users even after clearing cookies
 */

// Cache the fingerprint to avoid recalculating
let cachedFingerprint = null;

/**
 * Generate a simple hash from a string
 * @param {string} str - String to hash
 * @returns {string} - Hex hash
 */
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};

/**
 * Get canvas fingerprint
 * @returns {string} - Canvas fingerprint hash
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Draw text with specific font
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('9meals.ai', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('fingerprint', 4, 17);
    
    return simpleHash(canvas.toDataURL());
  } catch (e) {
    return 'canvas-error';
  }
};

/**
 * Get WebGL fingerprint
 * @returns {string} - WebGL renderer info
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return 'no-webgl';
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'no-debug-info';
    
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    
    return simpleHash(`${vendor}~${renderer}`);
  } catch (e) {
    return 'webgl-error';
  }
};

/**
 * Get installed fonts (basic detection)
 * @returns {string} - Font detection fingerprint
 */
const getFontFingerprint = () => {
  try {
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 
      'Georgia', 'Impact', 'Times New Roman', 'Trebuchet MS', 
      'Verdana', 'Helvetica', 'Palatino', 'Garamond'
    ];
    
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const getWidth = (fontFamily) => {
      ctx.font = `${testSize} ${fontFamily}`;
      return ctx.measureText(testString).width;
    };
    
    const baseWidths = baseFonts.map(getWidth);
    
    const detected = testFonts.filter((font) => {
      return baseFonts.some((baseFont, index) => {
        ctx.font = `${testSize} '${font}', ${baseFont}`;
        return ctx.measureText(testString).width !== baseWidths[index];
      });
    });
    
    return simpleHash(detected.join(','));
  } catch (e) {
    return 'fonts-error';
  }
};

/**
 * Generate browser fingerprint (SYNCHRONOUS version)
 * @returns {string} - Fingerprint hash
 */
export const getFingerprint = () => {
  // Return cached value if available
  if (cachedFingerprint) {
    return cachedFingerprint;
  }

  try {
    const components = [
      // Screen
      `${window.screen.width}x${window.screen.height}`,
      window.screen.colorDepth,
      window.devicePixelRatio || 1,
      
      // Timezone
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      new Date().getTimezoneOffset(),
      
      // Language
      navigator.language,
      (navigator.languages || []).join(','),
      
      // Platform
      navigator.platform,
      simpleHash(navigator.userAgent),
      
      // Hardware
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown',
      navigator.maxTouchPoints || 0,
      
      // Browser features
      navigator.cookieEnabled,
      
      // Canvas
      getCanvasFingerprint(),
      
      // WebGL
      getWebGLFingerprint(),
      
      // Fonts
      getFontFingerprint(),
    ];
    
    // Create combined hash
    const fingerprintString = components.join('|');
    cachedFingerprint = simpleHash(fingerprintString);
    
    return cachedFingerprint;
  } catch (e) {
    console.error('[Fingerprint] Error generating fingerprint:', e);
    return 'error-' + simpleHash(navigator.userAgent || 'unknown');
  }
};

/**
 * Generate complete browser fingerprint with details (ASYNC version)
 * @returns {Promise<Object>} - Fingerprint data with components
 */
export const generateFingerprint = async () => {
  const hash = getFingerprint();
  
  return {
    hash,
    components: {
      screen: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform,
      hardware: `${navigator.hardwareConcurrency || '?'}c-${navigator.deviceMemory || '?'}gb`,
    }
  };
};

export default { generateFingerprint, getFingerprint };
